import React, { useEffect, useId, useState } from "react";

import { XboxSpinner } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { fetchSupportJson, SupportRequestError } from "../../helpers/supportCases";
import { useStrings } from "../../localization/localizationHelpers";
import * as styles from "./CaseMatcherDebugPanel.module.css";

// Demo-only (spec 008). The debug endpoints don't exist in production, so the panel hides itself.

export type CaseAssociation = Readonly<{
  field: string;
  entity: string | null;
  id: string | null;
  name: string | null;
}>;

export type MatcherRunResult = Readonly<{
  caseId: string;
  ranAt: string;
  status: number;
  ok: boolean;
  body: string;
}>;

const prettyPrint = (body: string) => {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
};

export const CaseMatcherDebugPanel = ({ caseId }: { caseId: string }) => {
  const [
    title,
    associationsLabel,
    notSet,
    nameUnavailable,
    runLabel,
    rawOutput,
    statusLabel,
    ranAtLabel,
    crossAccountLabel,
  ] = useStrings(
    "caseDebug_title",
    "caseDebug_associations",
    "caseDebug_notSet",
    "caseDebug_nameUnavailable",
    "caseDebug_run",
    "caseDebug_rawOutput",
    "caseDebug_status",
    "caseDebug_ranAt",
    "caseDebug_crossAccount"
  );
  const titleId = useId();
  const [associations, setAssociations] = useState<readonly CaseAssociation[]>();
  const [result, setResult] = useState<MatcherRunResult>();
  const [running, setRunning] = useState(false);
  const [crossAccount, setCrossAccount] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAssociations(undefined);
    setResult(undefined);
    fetchSupportJson<{ associations: CaseAssociation[] }>(`/cases/${caseId}/debug/associations`)
      .then(response => !cancelled && setAssociations(response.associations))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  if (!associations) {
    return null;
  }

  const run = async () => {
    setRunning(true);
    try {
      setResult(
        await fetchSupportJson<MatcherRunResult>(`/cases/${caseId}/debug/matcher`, {
          method: "POST",
          body: JSON.stringify({ crossAccount }),
        })
      );
    } catch (error) {
      setResult({
        caseId,
        ranAt: new Date().toISOString(),
        status: error instanceof SupportRequestError ? error.status : 0,
        ok: false,
        body: (error as Error).message,
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <section aria-labelledby={titleId} className={styles.panel} data-testid="case-debug-panel">
      <h2 className={styles.title} id={titleId}>
        {title}
      </h2>
      <div className={styles.columns}>
        <div>
          <h3 className={styles.heading}>{associationsLabel}</h3>
          <dl className={styles.associations}>
            {associations.map(({ field, entity, id, name }) => (
              <div className={styles.association} key={field}>
                <dt>{field}</dt>
                <dd>
                  {id ? (
                    <>
                      {entity && <span>{entity}</span>} <code>{id}</code>{" "}
                      <span>{name ?? nameUnavailable}</span>
                    </>
                  ) : (
                    <span className={styles.muted}>{notSet}</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
        <div>
          <div className={styles.actions}>
            <BasicButton as="button" buttonType={ButtonType.Brand} disabled={running} onClick={run}>
              {runLabel}
            </BasicButton>
            <label>
              <input
                checked={crossAccount}
                disabled={running}
                type="checkbox"
                onChange={event => setCrossAccount(event.target.checked)}
              />{" "}
              {crossAccountLabel}
            </label>
            {running && <XboxSpinner />}
          </div>
          {result && (
            <div aria-live="polite" data-testid="case-debug-output">
              <h3 className={styles.heading}>{rawOutput}</h3>
              <p className={styles.meta}>
                {statusLabel}: {result.status} · {ranAtLabel}: {result.ranAt} ·{" "}
                <code>{result.caseId}</code>
              </p>
              <pre className={styles.output}>{prettyPrint(result.body)}</pre>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
